import { STAT_TILE_FILTER } from "ppt/constants";

/**
 * Compute the rollup that drives the lifecycle widget.
 *
 * The unit is **discounts by max severity**, sourced from
 * `promotionDetail.discountCountByStatus`. The backend already does the heavy
 * lifting:
 *   - rolls up parent-scoped AND child-scoped issues into a per-discount max
 *     severity (ERROR > WARNING > OK)
 *   - excludes inactive / soft-deleted discounts from the OK / WARNING / ERROR
 *     buckets and accounts for them under INACTIVE
 *
 * That means "issues on disabled discounts are excluded from the gate" is
 * already enforced server-side; we just surface it.
 *
 * (Earlier draft tried to read `promotionIssuesData.promotionLevel`, but that
 * endpoint returns *only* promotion-scoped issues — discount-level issues are
 * filtered out — so it always reported zero on real data.)
 */
export const computeRollup = (
  discountDetails = [],
  discountCountByStatus = null,
) => {
  const counts = discountCountByStatus || {};
  const activeErrors = counts.ERROR || 0;
  const activeWarnings = counts.WARNING || 0;
  const okCount = counts.OK || 0;
  const inactiveDiscountCount = counts.INACTIVE || 0;
  const activeDiscountCount = okCount + activeWarnings + activeErrors;
  const discountCount = activeDiscountCount + inactiveDiscountCount;

  // Sum children across active discounts only — disabled discounts shouldn't
  // pad the headline "Y client deals" number.
  const clientDeliveryCount = (discountDetails || [])
    .filter((d) => d?.active !== false)
    .reduce((sum, d) => sum + (d.children?.length || 0), 0);

  return {
    activeErrors,
    activeWarnings,
    okCount,
    discountCount,
    activeDiscountCount,
    inactiveDiscountCount,
    clientDeliveryCount,
  };
};

/**
 * Adapt a lifecycle rollup to the counts shape PPTStatTiles expects (keyed by
 * STAT_TILE_FILTER value). Keeps the rollup's discount-specific field names out
 * of the shared presentational component.
 */
export const rollupToStatTileCounts = (rollup) => ({
  [STAT_TILE_FILTER.OK]: rollup?.okCount ?? 0,
  [STAT_TILE_FILTER.WARNING]: rollup?.activeWarnings ?? 0,
  [STAT_TILE_FILTER.ERROR]: rollup?.activeErrors ?? 0,
  [STAT_TILE_FILTER.DISABLED]: rollup?.inactiveDiscountCount ?? 0,
});

/**
 * Health for the *current* stage.
 *   "error"      → at least one active discount with errors
 *   "warning"    → no errors but at least one warning
 *   "inProgress" → clean
 */
export const currentStageHealth = (rollup) => {
  if (rollup.activeErrors > 0) return "error";
  if (rollup.activeWarnings > 0) return "warning";
  return "inProgress";
};

/**
 * Filter the in-store discount list to those carrying a given severity.
 * Used to populate the AdvanceModal's "what's blocking" / "what's carrying
 * forward" list. We don't have a flat issue list across the promotion in the
 * API response shape we get, so we list affected discounts instead — which is
 * the right level of summary for the gating decision anyway. The discount's
 * Issues tab is the place to drill into specifics.
 */
export const discountsAtSeverity = (discountDetails, severity) =>
  (discountDetails || []).filter(
    (d) => d?.active !== false && d?.issueSeverity === severity,
  );
